export type ThemeMode = "system" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";

export function getSystemTheme(): ResolvedTheme {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return "dark";
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function resolveTheme(mode: ThemeMode): ResolvedTheme {
  if (mode === "system") {
    return getSystemTheme();
  }
  return mode;
}

// ─── Custom Theme System ─────────────────────────────────────────────────────

export type ThemePreset = "default" | "ocean" | "forest" | "sunset" | "midnight" | "custom";

export interface CustomTheme {
  accent: string;
  accentHover: string;
  accentMuted: string;
  accentSubtle: string;
  accentForeground: string;
  accentGlow: string;
  background: string;
  backgroundAccent: string;
  backgroundElevated: string;
  backgroundHover: string;
  backgroundMuted: string;
  card: string;
  cardForeground: string;
  text: string;
  textStrong: string;
  muted: string;
  border: string;
  fontFamily: string;
  fontSize: number; // base size in px (default 14)
}

export interface CustomThemeSettings {
  preset: ThemePreset;
  customTheme: CustomTheme | null;
  fontFamily: string;
  fontSize: number;
}

// Default preset themes
export const THEME_PRESETS: Record<Exclude<ThemePreset, "custom">, Omit<CustomTheme, never>> = {
  default: {
    accent: "#ff5c5c",
    accentHover: "#ff7070",
    accentMuted: "#ff5c5c",
    accentSubtle: "rgba(255, 92, 92, 0.15)",
    accentForeground: "#fafafa",
    accentGlow: "rgba(255, 92, 92, 0.25)",
    background: "#12141a",
    backgroundAccent: "#14161d",
    backgroundElevated: "#1a1d25",
    backgroundHover: "#262a35",
    backgroundMuted: "#262a35",
    card: "#181b22",
    cardForeground: "#f4f4f5",
    text: "#e4e4e7",
    textStrong: "#fafafa",
    muted: "#71717a",
    border: "#27272a",
    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
    fontSize: 14,
  },
  ocean: {
    accent: "#00b4d8",
    accentHover: "#33c4e3",
    accentMuted: "#00b4d8",
    accentSubtle: "rgba(0, 180, 216, 0.15)",
    accentForeground: "#fafafa",
    accentGlow: "rgba(0, 180, 216, 0.25)",
    background: "#0a1628",
    backgroundAccent: "#0d1a2d",
    backgroundElevated: "#122035",
    backgroundHover: "#1a2d42",
    backgroundMuted: "#1a2d42",
    card: "#0f1f38",
    cardForeground: "#e0f4ff",
    text: "#c4dff6",
    textStrong: "#f0f9ff",
    muted: "#5c8ab4",
    border: "#1a3a5c",
    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
    fontSize: 14,
  },
  forest: {
    accent: "#22c55e",
    accentHover: "#34d970",
    accentMuted: "#22c55e",
    accentSubtle: "rgba(34, 197, 94, 0.15)",
    accentForeground: "#fafafa",
    accentGlow: "rgba(34, 197, 94, 0.25)",
    background: "#0f1a10",
    backgroundAccent: "#121d13",
    backgroundElevated: "#172518",
    backgroundHover: "#213320",
    backgroundMuted: "#213320",
    card: "#152115",
    cardForeground: "#e8f5e9",
    text: "#c8e6c4",
    textStrong: "#f1f8f1",
    muted: "#6b8e6b",
    border: "#243824",
    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
    fontSize: 14,
  },
  sunset: {
    accent: "#f97316",
    accentHover: "#fb923c",
    accentMuted: "#f97316",
    accentSubtle: "rgba(249, 115, 22, 0.15)",
    accentForeground: "#fafafa",
    accentGlow: "rgba(249, 115, 22, 0.25)",
    background: "#1a1410",
    backgroundAccent: "#1e1812",
    backgroundElevated: "#251e16",
    backgroundHover: "#322820",
    backgroundMuted: "#322820",
    card: "#201812",
    cardForeground: "#fdf0e4",
    text: "#e8d4c0",
    textStrong: "#fef7f0",
    muted: "#9e8570",
    border: "#3a2c20",
    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
    fontSize: 14,
  },
  midnight: {
    accent: "#a855f7",
    accentHover: "#b879f9",
    accentMuted: "#a855f7",
    accentSubtle: "rgba(168, 85, 247, 0.15)",
    accentForeground: "#fafafa",
    accentGlow: "rgba(168, 85, 247, 0.25)",
    background: "#0d0d1a",
    backgroundAccent: "#101020",
    backgroundElevated: "#151528",
    backgroundHover: "#1e1e32",
    backgroundMuted: "#1e1e32",
    card: "#12121f",
    cardForeground: "#eee8ff",
    text: "#d4cce8",
    textStrong: "#f5f3ff",
    muted: "#7e7a9e",
    border: "#242438",
    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
    fontSize: 14,
  },
};

// Font family options
export const FONT_FAMILY_OPTIONS = [
  { value: "Inter, -apple-system, BlinkMacSystemFont, sans-serif", label: "Inter (default)" },
  { value: "system-ui, -apple-system, BlinkMacSystemFont, sans-serif", label: "System UI" },
  {
    value: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
    label: "SF Pro Display",
  },
  { value: "'Segoe UI', Roboto, Helvetica, sans-serif", label: "Segoe UI / Roboto" },
  { value: "Georgia, 'Times New Roman', serif", label: "Georgia / Serif" },
  { value: "'JetBrains Mono', ui-monospace, monospace", label: "JetBrains Mono" },
  { value: "'Fira Code', ui-monospace, monospace", label: "Fira Code" },
];

// Font size options (in px)
export const FONT_SIZE_OPTIONS = [
  { value: 12, label: "12px (Small)" },
  { value: 13, label: "13px" },
  { value: 14, label: "14px (Default)" },
  { value: 15, label: "15px" },
  { value: 16, label: "16px" },
  { value: 18, label: "18px (Large)" },
];

export const DEFAULT_CUSTOM_THEME_SETTINGS: CustomThemeSettings = {
  preset: "default",
  customTheme: null,
  fontFamily: FONT_FAMILY_OPTIONS[0].value,
  fontSize: 14,
};

const PRESET_KEYS = Object.keys(THEME_PRESETS) as Array<keyof typeof THEME_PRESETS>;

export function isValidPreset(value: string): value is ThemePreset {
  return value === "custom" || PRESET_KEYS.includes(value as keyof typeof THEME_PRESETS);
}

export function getPresetTheme(preset: ThemePreset, customTheme: CustomTheme | null): CustomTheme {
  if (preset === "custom" && customTheme) {
    return customTheme;
  }
  if (preset !== "custom" && preset in THEME_PRESETS) {
    return THEME_PRESETS[preset];
  }
  return THEME_PRESETS.default;
}

export function generateCssVariables(theme: CustomTheme, isDark: boolean): string {
  const vars = `
  --accent: ${theme.accent};
  --accent-hover: ${theme.accentHover};
  --accent-muted: ${theme.accentMuted};
  --accent-subtle: ${theme.accentSubtle};
  --accent-foreground: ${theme.accentForeground};
  --accent-glow: ${theme.accentGlow};
  --bg: ${theme.background};
  --bg-accent: ${theme.backgroundAccent};
  --bg-elevated: ${theme.backgroundElevated};
  --bg-hover: ${theme.backgroundHover};
  --bg-muted: ${theme.backgroundMuted};
  --card: ${theme.card};
  --card-foreground: ${theme.cardForeground};
  --text: ${theme.text};
  --text-strong: ${theme.textStrong};
  --muted: ${theme.muted};
  --border: ${theme.border};
  --font-body: ${theme.fontFamily};
  --font-size-base: ${theme.fontSize}px;
`;
  return vars.trim();
}

export function applyCustomTheme(theme: CustomTheme, isDark: boolean) {
  if (typeof document === "undefined") {
    return;
  }
  const root = document.documentElement;
  const cssVars = generateCssVariables(theme, isDark);
  // Parse and apply each CSS variable
  cssVars.split(";").forEach((decl) => {
    const trimmed = decl.trim();
    if (!trimmed) return;
    const colonIdx = trimmed.indexOf(":");
    if (colonIdx < 0) return;
    const prop = trimmed.slice(0, colonIdx).trim();
    const value = trimmed.slice(colonIdx + 1).trim();
    if (prop && value) {
      root.style.setProperty(prop, value);
    }
  });
}

export function exportThemeToJson(settings: CustomThemeSettings): string {
  return JSON.stringify(settings, null, 2);
}

export function importThemeFromJson(json: string): CustomThemeSettings | null {
  try {
    const parsed = JSON.parse(json) as Partial<CustomThemeSettings>;
    if (!parsed || typeof parsed !== "object") {
      return null;
    }
    return {
      preset: isValidPreset(parsed.preset ?? "") ? parsed.preset! : "default",
      customTheme:
        parsed.customTheme && typeof parsed.customTheme === "object" ? parsed.customTheme : null,
      fontFamily:
        typeof parsed.fontFamily === "string"
          ? parsed.fontFamily
          : DEFAULT_CUSTOM_THEME_SETTINGS.fontFamily,
      fontSize:
        typeof parsed.fontSize === "number" && parsed.fontSize >= 10 && parsed.fontSize <= 24
          ? parsed.fontSize
          : DEFAULT_CUSTOM_THEME_SETTINGS.fontSize,
    };
  } catch {
    return null;
  }
}
